import { hooks } from '@/store.js'
import { useMemo, useState } from 'react'
import { first, last } from 'lodash'
import { useElementWidth } from '@/lib/utils.js'
import {
  addDays,
  format,
  isToday,
  isSameWeek,
  isValid,
  isAfter,
  addWeeks,
  startOfToday,
  startOfDay,
  getWeekOfMonth,
} from 'date-fns'
import { AnimatePresence, Variants, motion } from 'framer-motion'
import { DateTitle } from './DateTitle.jsx'
import { AddNew } from './AddNew.jsx'
import { TitleInput } from './TitleInput.jsx'
import { DescriptionInput } from './DescriptionInput.jsx'
import { TimelineContent, TimelineHeader, getSevenDays, getTimelineDays } from './Timeline.jsx'
import { EditHeader } from './EditHeader.jsx'

const opacityAndMoveVariants: Variants = {
  exit: {
    y: -10,
    opacity: 0,
  },
  initial: {
    y: -10,
    opacity: 0,
  },
  animate: {
    y: 0,
    opacity: 1,
  },
}

const opacityVariants: Variants = {
  exit: {
    opacity: 0,
  },
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
  },
}

const storageKeys = {
  selectedDate: 'selectedDate',
}

export const Habits = () => {
  const [ref, wrapperWidth] = useElementWidth<HTMLDivElement>()
  const [state, _setState] = useState(() => {
    const selectedDateFromStorage = new Date(sessionStorage.getItem(storageKeys.selectedDate) ?? '')
    const date = isValid(selectedDateFromStorage) ? selectedDateFromStorage : new Date()

    return {
      selectedDate: date,
      prevDate: date,
    }
  })
  const [habitIdToEdit, setHabitIdToEdit] = useState<string | null>(null)

  const records = hooks.useAllRecords()
  const habits = hooks.useAllHabits()
  const habitToEdit = habits.find((habit) => habit.get('id') === habitIdToEdit)

  const direction = state.selectedDate > state.prevDate ? -1 : 1
  const todayFormatted = `${format(state.selectedDate, 'EEEE')}, ${format(state.selectedDate, 'dd.MM.yyyy')}`
  const dateTitle = isToday(state.selectedDate) ? 'Today' + `, ${todayFormatted}` : todayFormatted

  // TODO: change timeline to grid and use constants
  const daysNumber = Math.floor((wrapperWidth - (36 * 2 + 8)) / 40)
  const sevenDays = useMemo(() => getSevenDays(state.selectedDate), [state.selectedDate])
  const daysForEditTimeline = useMemo(
    () => getTimelineDays(state.selectedDate, daysNumber),
    [state.selectedDate, daysNumber],
  )

  const setSelectedDate = (date: Date) => {
    if (!isToday(date)) {
      sessionStorage.setItem(storageKeys.selectedDate, date.toISOString())
    } else {
      sessionStorage.removeItem(storageKeys.selectedDate)
    }

    _setState({
      selectedDate: date,
      prevDate: state.selectedDate,
    })
  }

  return (
    <div className="flex flex-1 flex-col gap-8 py-4" ref={ref}>
      <div className="flex items-center justify-between gap-2">
        <DateTitle direction={direction} dateTitle={dateTitle} />
        {habits.length > 0 && <AddNew />}
      </div>
      <div className="flex flex-1 flex-col">
        <AnimatePresence mode="popLayout" initial={false}>
          {habitToEdit ? (
            <motion.div
              key="edit-header"
              exit="exit"
              initial="initial"
              animate="animate"
              variants={opacityAndMoveVariants}
            >
              <EditHeader habit={habitToEdit} onBack={() => setHabitIdToEdit(null)} />
            </motion.div>
          ) : habits.length > 0 ? (
            <motion.div
              key="activity-header"
              className="relative flex justify-between"
              exit="exit"
              initial="initial"
              animate="animate"
              variants={opacityAndMoveVariants}
            >
              <div>
                {!isSameWeek(Date.now(), state.selectedDate, {
                  weekStartsOn: 1,
                }) && (
                  <button
                    className="absolute right-0 -mt-1 -translate-y-full transform text-sm text-gray-500"
                    onClick={() => {
                      setSelectedDate(new Date())
                    }}
                  >
                    Go to current week
                  </button>
                )}
              </div>
              <div className="hidden flex-1 items-center text-sm text-gray-500 sm:flex">
                Your activity{' '}
                {isSameWeek(Date.now(), state.selectedDate, {
                  weekStartsOn: 1,
                })
                  ? '(Last 7 days)'
                  : `(week ${getWeekOfMonth(state.selectedDate)} of ${format(state.selectedDate, 'MMMM')})`}
              </div>
              <TimelineHeader
                days={sevenDays}
                selectedDate={state.selectedDate}
                onDayClick={setSelectedDate}
                onPrevClick={() => {
                  setSelectedDate(addWeeks(state.selectedDate, -1))
                }}
                onNextClick={() => {
                  const today = startOfToday()
                  const targetDay = startOfDay(addWeeks(state.selectedDate, 1))

                  setSelectedDate(isAfter(targetDay, today) ? today : targetDay)
                }}
              />
            </motion.div>
          ) : null}
          {!habitToEdit && habits.length === 0 && (
            <motion.div
              layout
              key="empty-state"
              animate="animate"
              exit="exit"
              initial="initial"
              variants={opacityVariants}
              className="relative flex flex-col items-center justify-center gap-4 pt-10"
            >
              <div className="text-center">
                <p className="text-lg text-gray-500">You don't have any habits yet</p>
                <p className="text-sm text-gray-500">
                  Add your first habit to start tracking your progress
                </p>
              </div>
              <AddNew />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait" initial={false} custom={Boolean(habitToEdit)}>
          {habitToEdit && (
            <motion.div
              key="edit-title"
              layout
              layoutId={`habit-name-${habitIdToEdit}`}
              className="mt-2 w-full font-medium"
            >
              <TitleInput habit={habitToEdit} />
            </motion.div>
          )}

          {habitToEdit && (
            <motion.div
              key="edit-form"
              variants={opacityVariants}
              exit="exit"
              initial="initial"
              animate="animate"
              className="mt-4 flex flex-1 flex-col"
            >
              <TimelineHeader
                nextButtonAbsolute={false}
                days={daysForEditTimeline}
                selectedDate={state.selectedDate}
                onDayClick={setSelectedDate}
                onPrevClick={() => {
                  setSelectedDate(addDays(first(daysForEditTimeline)!, -1))
                }}
                onNextClick={() => {
                  setSelectedDate(addDays(last(daysForEditTimeline)!, 1))
                }}
              />
              <TimelineContent
                days={daysForEditTimeline}
                habitId={habitToEdit.get('id')}
                records={records}
                className="mt-1 justify-start pl-9"
              />
              <DescriptionInput habit={habitToEdit} />
            </motion.div>
          )}

          {!habitToEdit && habits.length !== 0 && (
            <motion.div key="activity-list" className="mt-2 space-y-2">
              {habits.map((habit) => {
                const habitId = habit.get('id')

                return (
                  <motion.div
                    key={habitId}
                    layout
                    className="flex items-center justify-between"
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    variants={{
                      animate: () => ({
                        y: 0,
                        opacity: 1,
                      }),
                      exit: (editing: boolean) => ({
                        y: editing ? 0 : -20,
                        opacity: editing ? 1 : 0,
                      }),
                      initial: (editing: boolean) => ({
                        y: editing ? 0 : 20,
                        opacity: editing ? 1 : 0,
                      }),
                    }}
                  >
                    <motion.div className="flex w-full min-w-0">
                      <motion.button
                        className="w-full max-w-full text-left font-medium"
                        onClick={() => {
                          setHabitIdToEdit(habitId)
                        }}
                      >
                        <motion.div
                          layout
                          layoutId={`habit-name-${habitId}`}
                          className="line-clamp-2 w-full leading-tight"
                        >
                          {habit.get('name') || 'Untitled habit'}
                        </motion.div>
                      </motion.button>
                    </motion.div>
                    <TimelineContent days={sevenDays} habitId={habitId} records={records} />
                  </motion.div>
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
